import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import DefaultLayout from '../layout/DefaultLayout';
import { PricingTable } from '../../shared/types';

interface VariationsData {
  pricingTable: PricingTable;
  template?: any;
  filledTemplates?: any[];
}

export function PricingTableVariationsPage() {
  const { id } = useParams<{ id: string }>();
  const [variations, setVariations] = useState<VariationsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariation, setSelectedVariation] = useState<string | number | null>(null);

  const fetchVariations = async () => {
    if (!id) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/pricing-tables/${id}/variations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to load variations');
      }

      const result = await response.json();
      setVariations(result.data);
      
      // Set the first filled template as selected by default, or the template if no filled templates
      if (result.data.filledTemplates.length > 0) {
        setSelectedVariation(result.data.filledTemplates[0].id);
      } else if (result.data.template) {
        setSelectedVariation('template');
      }
    } catch (error: any) {
      setError(error.message || 'Error loading variations');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVariations();
  }, [id]);

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  const getSelectedVariationContent = () => {
    if (!variations) return null;

    if (selectedVariation === 'template' && variations.template) {
      return {
        name: 'Original Template',
        content: variations.template.html_content,
        css: (variations.template as any).template_css?.css_content,
        date: variations.template.created_at,
        type: 'template'
      };
    }

    const filledTemplate = variations.filledTemplates.find(ft => ft.id === selectedVariation);
    if (filledTemplate) {
      return {
        name: `Filled Template - ${formatDate(filledTemplate.created_at)}`,
        content: filledTemplate.filled_html,
        css: (filledTemplate.template as any).template_css?.css_content,
        date: filledTemplate.created_at,
        type: 'filled'
      };
    }

    return null;
  };

  const selectedContent = getSelectedVariationContent();

  if (isLoading) return (
    <div className="p-4">Loading variations...</div>
  );

  if (error) return (
    <div className="p-4 text-red-500">Error loading variations: {error}</div>
  );

  if (!variations) return (
    <div className="p-4">No variations found.</div>
  );

  return (
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold mb-2">Pricing Table Variations</h1>
              <p className="text-gray-600 mb-2">{variations.pricingTable.name}</p>
              <p className="text-sm text-gray-500">{variations.pricingTable.description}</p>
            </div>
            <Link to="/pricing-tables">
              <Button variant="outline">Back to Pricing Tables</Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar with variations list */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Variations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {/* Original Template */}
                  {variations.template && (
                    <button
                      onClick={() => setSelectedVariation('template')}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedVariation === 'template'
                          ? 'bg-blue-50 border-blue-200 text-blue-700'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">Original Template</div>
                      <div className="text-sm text-gray-500">
                        {formatDate(variations.template.created_at)}
                      </div>
                    </button>
                  )}

                  {/* Filled Templates */}
                  {variations.filledTemplates.map((filledTemplate) => (
                    <button
                      key={filledTemplate.id}
                      onClick={() => setSelectedVariation(filledTemplate.id)}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedVariation === filledTemplate.id
                          ? 'bg-blue-50 border-blue-200 text-blue-700'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">Filled Template</div>
                      <div className="text-sm text-gray-500">
                        {formatDate(filledTemplate.created_at)}
                      </div>
                    </button>
                  ))}

                  {variations.filledTemplates.length === 0 && !variations.template && (
                    <div className="text-center text-gray-500 py-4">
                      No variations found
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main content area */}
          <div className="lg:col-span-3">
            {selectedContent ? (
              <Card>
                <CardHeader>
                  <CardTitle>{selectedContent.name}</CardTitle>
                  <p className="text-sm text-gray-500">
                    Created: {formatDate(selectedContent.date)}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* HTML Preview */}
                    <div>
                      <h3 className="text-lg font-semibold mb-2">HTML Preview</h3>
                      <div className="border rounded-lg p-4 bg-gray-50 max-h-96 overflow-auto">
                        <pre className="text-sm whitespace-pre-wrap">{selectedContent.content}</pre>
                      </div>
                    </div>

                    {/* CSS Preview */}
                    {selectedContent.css && (
                      <div>
                        <h3 className="text-lg font-semibold mb-2">CSS</h3>
                        <div className="border rounded-lg p-4 bg-gray-50 max-h-96 overflow-auto">
                          <pre className="text-sm whitespace-pre-wrap">{selectedContent.css}</pre>
                        </div>
                      </div>
                    )}

                    {/* Live Preview */}
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Live Preview</h3>
                      <div className="border rounded-lg p-4 bg-white">
                        <div 
                          dangerouslySetInnerHTML={{ __html: selectedContent.content }}
                          className="pricing-table-preview"
                        />
                        {selectedContent.css && (
                          <style dangerouslySetInnerHTML={{ __html: selectedContent.css }} />
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="text-center py-8">
                  <p className="text-gray-500">Select a variation to view its content</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
  );
}
